import { useEffect, useState } from 'react';
import { useGetOrCreateOnboardingSessionQuery } from '@/api/features/onboarding';
import { reduxLogger } from '@/core/utils/logger';
import { getErrorMessage } from '@voley/shared-utils';

interface UseMessagingEnabledReturn {
  isMessagingEnabled: boolean;
  isLoading: boolean;
  organizationId?: string;
}

/**
 * Hook to check if messaging features are enabled for the current user's organization.
 *
 * Messaging is enabled when:
 * - currentStep === 'account_qualified' OR
 * - status === 'completed'
 *
 * This is determined by the backend's `messagingEnabled` field in the onboarding session response.
 */
export function useMessagingEnabled(): UseMessagingEnabledReturn {
  const [isMessagingEnabled, setIsMessagingEnabled] = useState<boolean>(true); // Default to true to avoid flash
  const [organizationId, setOrganizationId] = useState<string | undefined>();

  // Fetch the current onboarding session
  const { data: session, isLoading, error } = useGetOrCreateOnboardingSessionQuery(undefined, {
    // Refetch when window regains focus to check if sales has activated messaging
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isLoading) {
      reduxLogger.info('[useMessagingEnabled] Loading onboarding session...');
      return;
    }

    if (error) {
      const errorObj = error as Record<string, unknown>;
      const errorDetails = {
        message: errorObj?.message || getErrorMessage(error),
        data: errorObj?.data,
        status: errorObj?.status,
        name: error instanceof Error ? error.name : undefined
      };
      reduxLogger.error('[useMessagingEnabled] Error fetching onboarding session:', errorDetails);
      // Default to ENABLED if we can't fetch session (fail open, not locked)
      queueMicrotask(() => {
        setIsMessagingEnabled(true);
      });
      return;
    }

    if (session) {
      reduxLogger.info('[useMessagingEnabled] Session data:', {
        currentStep: session.currentStep,
        status: session.status,
        organizationId: session.organizationId
      });

      // Simple check: messaging is enabled ONLY when currentStep is 'account_qualified' or status is 'completed'
      const enabled = session.currentStep === 'account_qualified' || session.status === 'completed';
      queueMicrotask(() => {
        setIsMessagingEnabled(enabled);
        setOrganizationId(session.organizationId);
      });

      reduxLogger.info(`[useMessagingEnabled] Messaging ${enabled ? 'ENABLED' : 'DISABLED'}`);
    } else {
      // No session found - default to ENABLED (backward compatibility)
      reduxLogger.warn('[useMessagingEnabled] No onboarding session found, defaulting to enabled');
      queueMicrotask(() => {
        setIsMessagingEnabled(true);
      });
    }
  }, [session, isLoading, error]);

  return {
    isMessagingEnabled,
    isLoading,
    organizationId
  };
}

